import { useQuery } from '@tanstack/react-query';

import { nonApiResponseFetch } from '@m/utils/http';

/** ~5m; SDK calls generateJWT again when it needs a fresh token. */
export const STALE_TIME_MS = 5 * 60 * 1000;

export const iterableJwtQueryKeys = {
  all: ['iterable', 'jwt'] as const,
  detail: (userId: number | undefined) => [...iterableJwtQueryKeys.all, userId] as const,
};

export type IterableJwtInitCredentials = {
  jwt: string;
};

type RawIterableJwtResponse = {
  data?: {
    jwt?: string;
    token?: string;
  };
  jwt?: string;
  token?: string;
};

export function parseIterableJwtCredentials(
  raw: RawIterableJwtResponse,
): IterableJwtInitCredentials {
  const inner = raw.data && typeof raw.data === 'object' ? raw.data : undefined;
  const jwt = raw.jwt ?? raw.token ?? inner?.jwt ?? inner?.token;

  if (typeof jwt !== 'string' || !jwt) {
    throw new Error('Iterable JWT endpoint returned no jwt or token');
  }

  return { jwt };
}

/**
 * GET /api/iterable/jwt (via SITE_API_BASE_URL + /iterable/jwt) with Keycloak auth.
 */
export async function fetchIterableJwtCredentials(): Promise<IterableJwtInitCredentials> {
  const raw = await nonApiResponseFetch<RawIterableJwtResponse>('/iterable/jwt', { method: 'GET' });
  return parseIterableJwtCredentials(raw);
}

export function useIterableJwtQuery(options: { enabled: boolean; userId: number | undefined }) {
  const { userId, enabled } = options;

  return useQuery({
    queryKey: iterableJwtQueryKeys.detail(userId),
    queryFn: fetchIterableJwtCredentials,
    enabled: enabled && userId !== undefined,
    staleTime: STALE_TIME_MS,
    gcTime: STALE_TIME_MS * 2,
    retry: 1,
  });
}
